/**
 * ReactRenderTracker (Track 1, 2026-05-13) — per-component render-count
 * telemetry for the React HUD. Pairs with FrameJitterTracker to expose
 * which components are re-rendering hot during play.
 *
 * Why this isn't React.Profiler:
 *   - <React.Profiler> only emits onRender data in dev builds OR when
 *     the profiling React bundle is shipped. Vite doesn't ship the
 *     profiling bundle for PTR, so Profiler is a runtime no-op there.
 *   - This singleton uses a manual counter (consumers call
 *     `useRenderCount(id)` at the top of their component), which works
 *     in any build.
 *
 * Default OFF. Enable via:
 *   - URL flag `?reactprofile=1`
 *   - `window.__REACT_RENDER_TRACKER__.setEnabled(true)` from DevTools
 *
 * When disabled, `record()` is an early-return — zero overhead in
 * production play.
 *
 * After enabling, FrameJitterOverlay's "React render rates" section
 * shows live renders/sec per registered component. LRG.24-pattern
 * React.memo candidates are anything sustaining >30 renders/sec.
 */

const URL_FLAG_NAME = 'reactprofile';
const STORAGE_KEY = 'h2k_react_profile';

interface RenderEntry {
  count: number;
  firstAt: number;
  lastAt: number;
}

import { browserOnly } from '../../shared/util/browserOnly';
import { bootSearchParams } from '../../config/boot/bootUrl';

class ReactRenderTrackerImpl {
  enabled = false;
  private counts = new Map<string, RenderEntry>();

  constructor() {
    browserOnly(() => {
      try {
        const sp = bootSearchParams();
        if (sp.get(URL_FLAG_NAME) === '1') this.enabled = true;
        else if (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === '1') {
          this.enabled = true;
        }
      } catch {
        /* SSR or restricted storage — leave disabled */
      }
    });
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) this.counts.clear();
    try {
      if (typeof localStorage !== 'undefined') {
        if (v) localStorage.setItem(STORAGE_KEY, '1');
        else localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }

  record(id: string): void {
    if (!this.enabled) return;
    const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const entry = this.counts.get(id);
    if (entry) {
      entry.count++;
      entry.lastAt = now;
    } else {
      this.counts.set(id, { count: 1, firstAt: now, lastAt: now });
    }
  }

  /** Stats sorted desc by rendersPerSec — top-N is the highest churn. */
  getStats(): Array<{ id: string; rendersPerSec: number; totalRenders: number; windowMs: number }> {
    const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const rows = Array.from(this.counts.entries()).map(([id, e]) => {
      const windowMs = Math.max(1, now - e.firstAt);
      return {
        id,
        rendersPerSec: e.count / (windowMs / 1000),
        totalRenders: e.count,
        windowMs,
      };
    });
    rows.sort((a, b) => b.rendersPerSec - a.rendersPerSec);
    return rows;
  }

  clear(): void {
    this.counts.clear();
  }
}

export const ReactRenderTracker = new ReactRenderTrackerImpl();

if (typeof window !== 'undefined') {
  (window as any).__REACT_RENDER_TRACKER__ = ReactRenderTracker;
}
